import { IBooking } from '@cnt-workspace/models';
import { parseBookingDate } from '../shared/iso-date.util';

/** Format a Date as a basic-form ICS UTC stamp (YYYYMMDDTHHmmssZ). */
function icsTimestamp(d: Date): string {
  return d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
}

/** Basic-form ICS DATE value (YYYYMMDD) from the date's LOCAL calendar day. */
function icsDate(iso: string): string {
  const d = parseBookingDate(iso) ?? new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Build a single-event VCALENDAR string for a booking. Caller decides what to
 * do with it.
 *
 * The stay is an ALL-DAY event, so it takes DATE values rather than an
 * instant. It used to emit `DTSTART:20260620T000000Z`, which the guest's
 * calendar rendered at 8pm on the 19th west of UTC — and at a different hour
 * in each timezone. A DATE value has no timezone to get wrong. DTEND is
 * exclusive for all-day events, which is exactly what `dates.end` already is:
 * the checkout day. See F17.
 */
export function buildBookingIcs(b: IBooking): string {
  return [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//CurbNTurf//Booking//EN',
    'BEGIN:VEVENT',
    `UID:${b.id}@curbnturf`,
    `DTSTAMP:${icsTimestamp(new Date())}`,
    `DTSTART;VALUE=DATE:${icsDate(b.dates.start)}`,
    `DTEND;VALUE=DATE:${icsDate(b.dates.end)}`,
    `SUMMARY:CurbNTurf — ${b.listingTitle}`,
    `DESCRIPTION:Hosted by ${b.hostName}.`,
    `LOCATION:${b.listingLocation}`,
    'END:VEVENT', 'END:VCALENDAR',
  ].join('\r\n');
}

/** Browser-only: trigger a download of the booking's .ics file. No-op outside the browser. */
export function downloadBookingIcs(b: IBooking): void {
  if (typeof document === 'undefined') return;
  const ics = buildBookingIcs(b);
  const blob = new Blob([ics], { type: 'text/calendar' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `curbnturf-${b.id.slice(0, 8)}.ics`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
